import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { injectJsonLd, ORGANIZATION_JSON_LD } from "../../lib/jsonLd.js";
import TestimonialsCarousel from "./Carrousels/TestimonialCarrousel.js";
import AnimalCarrousel from "./Carrousels/AnimalCarrousel.js";
import HeroSection from "./Hero/Hero.js";
import PawMatchBanner from "./Banner/PawMatchBanner.js";
import FaqAccordion from "./FaqAccordion.js";
import Aurora from "../../Components/Aurora";
import LightRays from "../../Components/LightRays";
import HelpPawseumModal from "../../Components/HelpPawseumModal.js";
import {
  CatIconFilled,
  DogIconFilled,
} from "../../shared/Icons/CustomIcons.js";
import { Heart } from "lucide-react";

// Editable heart placement data (top-level for easy edits)
const smallHearts = [
  {
    src: "/assets/smallHearts.webp",
    width: "10rem",
    top: "15%",
    right: "30%",
    rotate: -8,
    opacity: 0.7,
  },
  {
    src: "/assets/smallHearts.webp",
    width: "12.5rem",
    top: "25%",
    right: "10%",
    rotate: 7,
    opacity: 0.75,
  },
  {
    src: "/assets/smallHearts.webp",
    width: "11.5rem",
    bottom: "20%",
    left: "10%",
    rotate: 4,
    opacity: 0.65,
  },
];

const bigHearts = [
  {
    src: "/assets/bigHearts.webp",
    width: "12rem",
    bottom: "15%",
    right: "10%",
    rotate: 10,
    opacity: 0.75,
  },
  {
    src: "/assets/bigHearts.webp",
    width: "11rem",
    top: "25%",
    left: "10%",
    rotate: -4,
    opacity: 0.6,
  },
];

const LandingAnimatedBackdrop = React.memo(() => (
  <>
    <div className="absolute inset-0  pointer-events-none">
      <Aurora
        colorStops={["#c40e0e", "#6036a1", "#ad2300"]}
        amplitude={0.5}
        blend={1}
      />
    </div>
    <div className="absolute inset-0  pointer-events-none">
      <LightRays
        raysOrigin="top-center"
        raysColor="#ffffff"
        mouseInfluence={0.1}
        saturation={3}
        lightSpread={1.5}
        className="opacity-60"
      />
    </div>
    <div className="absolute w-full h-[120%] overflow-hidden bottom-0 left-0 pointer-events-none">
      {/* Desktop: multiple decorative hearts (behind content) */}
      <div
        className="hidden md:block absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        <div className="absolute inset-0 z-0">
          {smallHearts.map((h, i) => (
            <img
              key={`small-${i}`}
              src={h.src}
              alt=""
              className="absolute"
              style={{
                width: h.width,
                opacity: h.opacity,
                transform: `rotate(${h.rotate}deg)`,
                top: h.top as any,
                left: (h as any).left as any,
                right: (h as any).right as any,
                bottom: (h as any).bottom as any,
              }}
            />
          ))}

          {bigHearts.map((h, i) => (
            <img
              key={`big-${i}`}
              src={h.src}
              alt=""
              className="absolute"
              style={{
                width: h.width,
                opacity: h.opacity,
                transform: `rotate(${h.rotate}deg)`,
                top: h.top as any,
                left: (h as any).left as any,
                right: (h as any).right as any,
                bottom: (h as any).bottom as any,
              }}
            />
          ))}
        </div>
      </div>

      {/* Mobile single centered heart (unchanged) */}
      <div
        className=" md:hidden absolute bottom-10 inset-0 opacity-60 flex items-start justify-center"
        style={{
          backgroundImage: "url('/assets/heartBg.png')",
          backgroundRepeat: "no-repeat", // mobile: single centered image
          backgroundSize: "100% auto%",
          backgroundPosition: "center center",
        }}
        aria-hidden="true"
      />
    </div>
  </>
));

const LandingPage = () => {
  const { t } = useTranslation("landing");
  const navigate = useNavigate();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  useEffect(() => {
    return injectJsonLd("org", ORGANIZATION_JSON_LD)
  }, []);

  const handleSearch = (petType: "cao" | "gato", location: string) => {
    navigate(petType === "cao" ? "/caes" : "/gatos", {
      state: { petType, location },
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 bg-landing-gradient">
        <HeroSection />
        <PawMatchBanner />
        <section className="relative overflow-hidden px-4  bg-black/90 sm:px-7 flex flex-col items-center p-4 md:p-6 text-center animate-fadeInUp">
          {/* <div className="absolute inset-0 z-0 pointer-events-none">
            <SilkBackground
              color="#d23030000"
              speed={5}
              scale={1.1}
              noiseIntensity={1.2}
              rotation={0.2}
            />
          </div> */}

          <LandingAnimatedBackdrop />
          <div className="relative z-10 w-full">
            <div className="text-xl px-6 md:leading-none leading-5 sm:text-2xl md:text-xl mb-3 md:mb-20">
              <h2 className="font-bold pt-5 text-3xl md:text-4xl text-white">
                {/* Mobile text */}
                <span className="md:hidden text-white">
                  {t("page.heartWinners")}
                </span>
                {/* Desktop text */}
                <span className="hidden md:inline text-white">
                  {t("page.heartWinners")}
                </span>
              </h2>
              <span>
                {/* Mobile text */}
                <span className="md:hidden text-white">
                  {t("page.mostLovedMobile")}
                </span>
                {/* Desktop text */}
                <span className="hidden md:inline text-white/80">
                  {t("page.mostLovedDesktop")}
                </span>
              </span>
            </div>
            <div className="relative z-10">
              <AnimalCarrousel />
            </div>
            {/* <TestimonialsCarousel /> */}
            <h2 className="text-2xl sm:text-3xl mt-20 md:text-4xl m-3 sm:mb-8 py-4 text-white animate-fadeInUp">
              {/* Mobile text */}
              {t("page.readyToMeet")}
            </h2>
            {/* Mobile images (centered, small) */}

            {/* Decorative left-side image (visible on md+) */}
            <div className="flex flex-col justify-center items-center sm:flex-row gap-4 sm:gap-10 mb-20 sm:mb-16">
              <button
                onClick={() => navigate("/caes")}
                className="flex items-center  w-fit  bg-main-purple text-white px-4 sm:px-4 py-3 sm:py-4 rounded-lg font-bold text-base md:text-xl transition-all duration-300 shadow-lg animate-slideInRight hover:bg-warm-accent-hover hover:-translate-y-1 hover:shadow-xl hover:bg-opacity-80"
              >
                {/* Mobile text */}
                <DogIconFilled size={30} />
                <span className="inline pl-3">{t("page.seeAllDogs")}</span>
              </button>
              <div className="md:hidden absolute flex left-3 items-center justify-between w-full bottom-0">
                <img
                  src="/assets/dog-lines.webp"
                  alt={t("page.decorativeDogsAlt")}
                  className="w-32 md:block hidden scale-[115%] h-auto pointer-events-none"
                  aria-hidden="true"
                />
                <img
                  src="/assets/cat-lines.webp"
                  alt={t("page.decorativeCatsAlt")}
                  className="w-32 md:block hidden scale-110 h-auto pointer-events-none"
                  aria-hidden="true"
                />
              </div>
              <button
                onClick={() => navigate("/gatos")}
                className="flex items-center justify-start w-fit bg-main-blue text-white px-4 sm:px-4 py-3 sm:py-4 rounded-lg font-bold text-base md:text-xl transition-all duration-300 shadow-lg animate-slideInRight hover:bg-warm-accent-hover hover:-translate-y-1 hover:shadow-xl hover:bg-opacity-80"
              >
                <CatIconFilled size={28} />
                {/* Mobile text */}

                {/* Desktop text */}
                <span className="inline pl-3">{t("page.seeAllCats")}</span>
              </button>
            </div>
          </div>
        </section>
        <section className="relative z-10 mb-20 mt-10 md:mt-20">
          <FaqAccordion />
        </section>
      </main>

      {/* Help Modal */}
      <HelpPawseumModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
};

export default LandingPage;
